import React, { useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Code,
  Collapse,
  Icon,
  IconButton,
  Tooltip,
  Spinner,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiCheck, FiX, FiMinus, FiCopy, FiAlertTriangle } from 'react-icons/fi';
import { useCampaignContext } from './CampaignContext';

const STATUS_BADGE = {
  success: { color: 'green', label: 'Test OK' },
  executed: { color: 'green', label: 'Executed' },
  would_skip: { color: 'yellow', label: 'Would Skip' },
  blocked: { color: 'orange', label: 'Blocked' },
  soft_failed: { color: 'orange', label: 'Soft-failed (continued)' },
  error: { color: 'red', label: 'Failed' },
};

function DataRow({ label, value, highlight }) {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const valueBg = highlight ? highlightBg : 'transparent';

  if (value === undefined || value === null) return null;

  const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  const isLong = displayValue.length > 100;

  return (
    <Flex gap={2} py={1} px={2} borderRadius="sm" bg={valueBg} align="start">
      <Text fontSize="xs" color={labelColor} fontWeight="medium" minW="100px" flexShrink={0}>
        {label}
      </Text>
      {isLong ? (
        <Code fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-all" flex={1} p={1} borderRadius="sm">
          {displayValue}
        </Code>
      ) : (
        <Text fontSize="xs" flex={1} wordBreak="break-all">
          {displayValue || <Text as="span" color="gray.300">(empty)</Text>}
        </Text>
      )}
    </Flex>
  );
}

function ContextSection({ title, data, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const headerBg = useColorModeValue('gray.50', 'gray.750');

  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;

  const entries = typeof data === 'object' ? Object.entries(data) : [];

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={2}>
      <Flex
        align="center"
        gap={2}
        px={2}
        py={1.5}
        bg={headerBg}
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ opacity: 0.8 }}
      >
        <Icon as={isOpen ? FiChevronDown : FiChevronRight} boxSize={3} color="gray.500" />
        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500">
          {title}
        </Text>
        <Badge fontSize="9px" colorScheme="gray" ml="auto">{entries.length} fields</Badge>
      </Flex>
      <Collapse in={isOpen}>
        <VStack align="stretch" spacing={0} p={1}>
          {entries.map(([key, val]) => (
            <DataRow key={key} label={key} value={val} />
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}

function InputPanel({ input }) {
  if (!input) {
    return (
      <Text fontSize="xs" color="gray.400" py={4} textAlign="center">
        Run a test to see input data
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={1} py={2}>
      <ContextSection title="Contact" data={input.contact} defaultOpen />
      <ContextSection title="Deal" data={input.deal} />
      <ContextSection title="Sender" data={input.sender} />
      {input.upstreamNodes && Object.keys(input.upstreamNodes).length > 0 && (
        <ContextSection title="Upstream Node Outputs" data={input.upstreamNodes} />
      )}
    </VStack>
  );
}

function OutputPanel({ output, subType }) {
  if (!output) {
    return (
      <Text fontSize="xs" color="gray.400" py={4} textAlign="center">
        Run a test to see output data
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={2} py={2}>
      <Box px={2}>
        <Text fontSize="sm" fontWeight="medium" mb={1}>{output.summary}</Text>
      </Box>

      {output.resolvedContent && Object.keys(output.resolvedContent).length > 0 && (
        <ContextSection title="Resolved Content" data={output.resolvedContent} defaultOpen />
      )}

      {output.details && Object.keys(output.details).length > 0 && (
        <ContextSection title="Details" data={output.details} />
      )}
    </VStack>
  );
}

/**
 * NodeDataPanel — collapsible input/output panel below each node card.
 * Shows the data flowing in and out after a test has been run.
 */
export default function NodeDataPanel({ nodeId, subType, isCompact = false }) {
  const { testResults, testRunning } = useCampaignContext();
  const result = testResults[nodeId];
  const isRunning = testRunning === nodeId;

  const panelBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (isRunning) {
    return (
      <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={3} mt={1} maxW="480px" w="100%">
        <Flex align="center" gap={2} justify="center">
          <Spinner size="sm" color="blue.400" />
          <Text fontSize="xs" color="gray.500">Testing node...</Text>
        </Flex>
      </Box>
    );
  }

  if (!result) return null;

  const statusInfo = STATUS_BADGE[result.status] || STATUS_BADGE.success;

  // Compact mode: single summary line
  if (isCompact) {
    const fieldCount = result.input ? Object.values(result.input).reduce((sum, obj) => {
      return sum + (typeof obj === 'object' && obj ? Object.keys(obj).length : 0);
    }, 0) : 0;

    return (
      <Flex
        bg={panelBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        px={3}
        py={1.5}
        mt={1}
        maxW="480px"
        w="100%"
        align="center"
        gap={2}
      >
        <Icon
          as={result.status === 'error' ? FiX : result.status === 'soft_failed' ? FiAlertTriangle : result.status === 'would_skip' ? FiMinus : FiCheck}
          color={`${statusInfo.color}.500`}
          boxSize={3}
        />
        <Text fontSize="xs" color="gray.500" flex={1} noOfLines={1}>
          Input: {fieldCount} fields | {result.output?.summary || 'No output'}
        </Text>
        <Badge colorScheme={statusInfo.color} fontSize="9px">{statusInfo.label}</Badge>
      </Flex>
    );
  }

  return (
    <Box
      bg={panelBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      mt={1}
      maxW="480px"
      w="100%"
      overflow="hidden"
    >
      <Flex px={3} py={2} align="center" justify="space-between" borderBottomWidth="1px" borderColor={borderColor}>
        <HStack spacing={2}>
          <Icon
            as={result.status === 'error' ? FiX : result.status === 'soft_failed' ? FiAlertTriangle : result.status === 'would_skip' ? FiMinus : FiCheck}
            color={`${statusInfo.color}.500`}
            boxSize={4}
          />
          <Badge colorScheme={statusInfo.color} fontSize="xs">{statusInfo.label}</Badge>
        </HStack>
        <HStack spacing={1}>
          {result.timing?.durationMs !== undefined && (
            <Text fontSize="xs" color="gray.400">{result.timing.durationMs}ms</Text>
          )}
          <Tooltip label="Copy test data as JSON" placement="top">
            <IconButton
              icon={<FiCopy />}
              size="xs"
              variant="ghost"
              aria-label="Copy JSON"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify({ input: result.input, output: result.output, status: result.status }, null, 2));
              }}
            />
          </Tooltip>
        </HStack>
      </Flex>

      <Tabs size="sm" variant="soft-rounded" colorScheme="gray" px={2} pt={2}>
        <TabList>
          <Tab fontSize="xs" py={1}>Input</Tab>
          <Tab fontSize="xs" py={1}>Output</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} py={2}>
            <InputPanel input={result.input} />
          </TabPanel>
          <TabPanel px={0} py={2}>
            <OutputPanel output={result.output} subType={subType} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
